import { useEffect, useState } from 'react'
import { useTexte, WORTLAUT } from '../../texte'
import { Navigate } from 'react-router-dom'
import { rueckfrage } from '../../components/Rueckfrage'
import { Kontoloeschung } from './Kontoloeschung'
import { usePreference } from '../../lib/preference'
import { datumKurz, datumText } from '../../lib/ortszeit'

import {
  useMe,
  usePeople,
  useSetDisabled,
  useSetPersonName,
  useSetRole,
  useSetSupervisor,
} from '../../hooks/queries'
import type { AccountRole, Person } from '../../api/types'

/**
 * Wer darf eigene Reiseberichte schreiben.
 *
 * Sichtbar nur für die Verwaltung — für alle anderen wäre diese Liste ein
 * Verzeichnis aller Konten. Der Server verweigert sie ohnehin; hier wird sie
 * gar nicht erst angefragt.
 */
/**
 * Der alte Weg hierher.
 *
 * Seit #39 sitzt die Personenliste als Kategorie in der Administration. Die Route
 * bleibt trotzdem bestehen: sie steht in Lesezeichen, in älteren Verweisen und
 * in einem Browserlauf. Ein Weg, den es gab, führt weiter hin -- nur eben
 * dorthin, wo die Sache jetzt steht.
 */
export function PeoplePage() {
  return <Navigate to="/settings/administration?bereich=personen" replace />
}

/**
 * What the list can be sorted by (#97). The order here is the order of the
 * pills. Since 1.92.0 (#117) two moments instead of one "zuletzt aktiv": in
 * the app, and written -- a reader who only reads stood there as "never".
 */
type Sortierfeld = 'name' | 'email' | 'beigetreten' | 'app' | 'geschrieben' | 'rolle'
const SORTIERFELDER: readonly Sortierfeld[] = ['name', 'email', 'beigetreten', 'app', 'geschrieben', 'rolle']

/**
 * A remembered choice from before 1.92.0: "aktiv" meant "last written", and
 * it stays that -- a remembered sort order should not jump to the name.
 */
function ausGemerkt(feld: string): Sortierfeld {
  if (feld === 'aktiv') return 'geschrieben'
  return (SORTIERFELDER as readonly string[]).includes(feld) ? (feld as Sortierfeld) : 'name'
}

/** A moment that may be missing: a missing one stays at the end in both directions. */
function nachZeitpunkt(a: string | null, b: string | null, richtung: number): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return richtung * a.localeCompare(b)
}

/** The ladder of roles, so "sort by role" puts the administration at one end. */
const RANG: Record<AccountRole, number> = { reader: 0, author: 1, admin: 2 }

/**
 * Which name the list sorts and shows: the one the administration gave, else
 * the person's own. Sorting by the *shown* name is what a person expects who
 * reads the list -- sorting by the hidden one would look like a wrong order.
 */
function sortname(person: Person): string {
  return person.admin_display_name ?? person.display_name
}

/**
 * Compare two people by one field, ascending. The direction is applied by
 * the caller -- with one exception written into the field itself: someone
 * who never wrote anything has no "last active" and stays at the end in
 * **both** directions. A missing date is not an old date.
 */
function vergleiche(a: Person, b: Person, feld: Sortierfeld, ab: boolean, gebiet: string): number {
  const richtung = ab ? -1 : 1
  switch (feld) {
    case 'name':
      return richtung * sortname(a).localeCompare(sortname(b), gebiet, { sensitivity: 'base' })
    case 'email':
      return richtung * a.email.localeCompare(b.email, gebiet, { sensitivity: 'base' })
    case 'beigetreten':
      return richtung * a.created_at.localeCompare(b.created_at)
    case 'app':
      return nachZeitpunkt(a.last_seen_at, b.last_seen_at, richtung)
    case 'geschrieben':
      return nachZeitpunkt(a.last_active_at, b.last_active_at, richtung)
    case 'rolle':
      return richtung * (RANG[a.role] - RANG[b.role])
  }
}

/**
 * Die Personenliste selbst -- **ohne Kopfzeile und ohne Scrollrahmen**.
 *
 * Beides bringt seit #39 die Kategorienansicht mit. Stünde es hier noch
 * einmal, gäbe es zwei Kopfzeilen übereinander und zwei Bereiche, die
 * unabhängig voneinander scrollen.
 *
 * **Seit #97 eine Tabelle, die am Telefon eine Liste ist.** Aus dem Betrieb:
 * die Liste war ein Stapel offener Formulare, jede Person mit Feld, Auswahl
 * und drei Knöpfen -- bei zwanzig Konten zwanzig Bildschirme, und sortieren
 * ließ sich nichts. Jetzt steht je Person **eine Zeile** (Name, E-Mail,
 * beigetreten, zuletzt aktiv, Rolle), und die Bedienung klappt darunter auf,
 * eine Zeile auf einmal. Sortiert wird über eine Reihe Pillen -- dieselbe
 * Bauart wie die Reiter der Werkbank auf der Karte (`.pillenzeile`,
 * `.pille`), damit es am Telefon wie am Schreibtisch dasselbe Bild ist:
 * eine Reihe, die seitwärts scrollt, statt eines Tabellenkopfs, der auf 390
 * Punkten nicht hinpasst. Auf breiten Fenstern steht der Kopf zusätzlich
 * über den Spalten (`.personen__kopf`), als Beschriftung, nicht als zweiter
 * Sortierweg -- zwei Wege für dieselbe Frage liefen auseinander.
 */
export function Personenliste() {
  const t = useTexte()
  const me = useMe()
  const darfVerwalten = me.data?.may_manage_people === true
  const people = usePeople(darfVerwalten)
  const setRole = useSetRole()
  const setDisabled = useSetDisabled()
  const setName = useSetPersonName()
  const setSupervisor = useSetSupervisor()
  // Nur ein Supervisor gibt das Merkmal weiter -- der Server entscheidet das
  // (`requireSupervisor`); hier wird das Häkchen nur nicht angeboten.
  const darfSupervisor = me.data?.may_operate === true
  /*
   * **Was gelöscht wurde, steht über der Liste und nicht in der Zeile.**
   *
   * Der Satz stand bis hierher in der Zeile der gelöschten Person -- und die
   * verschwindet im selben Augenblick, weil die Liste neu lädt. Er war damit
   * für einen Wimpernschlag da oder gar nicht; der Browserlauf
   * (`kontoloeschen.mjs`) wartete fünfzehn Sekunden auf ihn und lief in eine
   * Zeitüberschreitung. Eine Meldung, die man nicht lesen kann, ist keine.
   *
   * Er bleibt stehen, bis das nächste Konto gelöscht wird: was weg ist, ist
   * weg, und die Zahlen dazu will man in Ruhe nachlesen können.
   */
  const [geloescht, setGeloescht] = useState<string | null>(null)

  /*
   * The sort order is a matter of the device, not the account (#97): on the
   * phone one wants the recently active ones, at the desk the alphabet. It
   * therefore lives in `localStorage`, like the folded trips of the chat
   * list. Tapping the active pill again turns the direction around.
   */
  const [sortierung, setSortierung] = usePreference<{ feld: Sortierfeld; ab: boolean }>(
    'tb.personen.sortierung',
    { feld: 'name', ab: false },
  )
  // A key from an older build that no longer exists falls back to the name
  // instead of leaving the list unsorted.
  const feld = ausGemerkt(sortierung.feld)
  const ab = sortierung.ab === true

  /* One row open at a time. The open row carries the whole form; two open
     rows were the old picture, twenty were the complaint. */
  const [offen, setOffen] = useState<number | null>(null)

  const sortiert = [...(people.data ?? [])].sort((a, b) => vergleiche(a, b, feld, ab, t.allgemein.gebiet))

  const pillenname: Record<Sortierfeld, string> = {
    name: t.personen.sortName,
    email: t.personen.sortEmail,
    beigetreten: t.personen.sortBeigetreten,
    app: t.personen.sortInApp,
    geschrieben: t.personen.sortGeschrieben,
    rolle: t.personen.sortRolle,
  }

  return (
    <>
        <div className="card card--seite card--breit">
          <p className="card__lead card__lead--dicht">{t.personen.lead}</p>
        </div>

        {(setRole.isError || setDisabled.isError || setName.isError || setSupervisor.isError) && (
          <p className="notice notice--mittig" role="alert">
            {(setRole.error ?? setDisabled.error ?? setName.error ?? setSupervisor.error)?.message}
          </p>
        )}

        {geloescht !== null && (
          <p
            className="notice notice--mittig"
            role="status"
            /* Der Satz ist zusammengesetzt (Name und zwei Zahlen); ein
               Prüflauf käme sonst nur über ein Stück davon heran (1.23.0). */
            data-geloescht=""
          >
            {geloescht}
          </p>
        )}

        <div className="card card--seite card--dicht card--breit personen">
          {people.isPending && <p className="card__lead">{t.personen.wirdGeladen}</p>}

          <div className="pillenzeile" role="group" aria-label={t.personen.sortieren}>
            {SORTIERFELDER.map((f) => (
              <button
                key={f}
                type="button"
                className="pille"
                aria-pressed={f === feld}
                aria-label={`${pillenname[f]}, ${f === feld && ab ? t.personen.absteigend : t.personen.aufsteigend}`}
                data-sortierfeld={f}
                data-richtung={f === feld ? (ab ? 'ab' : 'auf') : undefined}
                onClick={() => setSortierung(f === feld ? { feld: f, ab: !ab } : { feld: f, ab: false })}
              >
                {pillenname[f]}
              </button>
            ))}
          </div>

          {/* Kopf und Liste in einem Raster (1.49.3): die Spalten werden einmal
              gemessen, jede Zeile nimmt sie als Subgrid -- so stehen sie bündig. */}
          <div className="personen__tabelle">
          <div className="personen__kopf" aria-hidden="true">
            <span data-spalte="name">{t.personen.sortName}</span>
            <span data-spalte="email">{t.personen.sortEmail}</span>
            <span data-spalte="beigetreten">{t.personen.sortBeigetreten}</span>
            {/* Two moments, one column (1.92.0, #117): a sixth column does
                not fit beside the settings list on an iPad (measured, 77
                points over the card). The header names both lines. */}
            <span data-spalte="zuletzt" className="personen__zuletzt">
              <span>{t.personen.sortInApp}</span>
              <span>{t.personen.sortGeschrieben}</span>
            </span>
            <span data-spalte="rolle">{t.personen.sortRolle}</span>
          </div>

          <div className="personen__liste">
            {sortiert.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                self={person.id === me.data?.id}
                offen={offen === person.id}
                onOffen={() => setOffen(offen === person.id ? null : person.id)}
                busy={
                  setRole.isPending ||
                  setDisabled.isPending ||
                  setName.isPending
                }
                onChange={(role) => setRole.mutate({ id: person.id, role })}
                onDisabled={(disabled) => setDisabled.mutate({ id: person.id, disabled })}
                onName={(name) => setName.mutate({ id: person.id, name })}
                onGeloescht={setGeloescht}
                darfSupervisor={darfSupervisor}
                onSupervisor={(supervisor) => setSupervisor.mutate({ id: person.id, supervisor })}
              />
            ))}
          </div>
          </div>
        </div>
    </>
  )
}

function PersonRow({
  person,
  self,
  offen,
  onOffen,
  busy,
  onChange,
  onDisabled,
  onName,
  onGeloescht,
  darfSupervisor,
  onSupervisor,
}: {
  person: Person
  self: boolean
  /** Whether the form below the row is open (#97) -- one at a time. */
  offen: boolean
  onOffen: () => void
  busy: boolean
  onChange: (role: AccountRole) => void
  onDisabled: (disabled: boolean) => void
  onName: (name: string) => void
  /** Was gelöscht wurde -- gezeigt wird es über der Liste, nicht hier. */
  onGeloescht: (befund: string) => void
  /** Ob der Betrachter Supervisor ist und das Merkmal vergeben darf (#62). */
  darfSupervisor: boolean
  onSupervisor: (supervisor: boolean) => void
}) {
  const t = useTexte()
  // Der Entwurf bleibt im Feld, bis er hinausgeht. Ein Feld, das bei jedem
  // Buchstaben sichert, ist derselbe Fehler wie beim Regler des Deckgrads.
  const [entwurf, setEntwurf] = useState(person.admin_display_name ?? '')
  useEffect(() => {
    setEntwurf(person.admin_display_name ?? '')
  }, [person.admin_display_name])

  const angezeigt = person.admin_display_name ?? person.display_name

  const sichere = () => {
    const wert = entwurf.trim()
    if (wert === (person.admin_display_name ?? '')) return
    onName(wert)
  }

  return (
    <div className="personen__eintrag" data-person={person.id} data-person-offen={offen ? '' : undefined}>
      {/* The row is one button: on the phone the whole line is the target,
          and 44 points high it is anyway. The columns are spans with
          `data-spalte`, so the stylesheet can lay them out as a grid at the
          desk and stack them on the phone -- and a probe finds them there. */}
      <button
        type="button"
        className="personen__zeile"
        aria-expanded={offen}
        aria-label={t.personen.zeileOeffnen(angezeigt)}
        onClick={onOffen}
      >
        <span data-spalte="name" className="fett">
          {angezeigt}
          {self && (
            <span className="gedaempft mager">{t.personen.du}</span>
          )}
          {person.disabled && (
            <span className="warnend mager">
              {t.personen.stillgelegt}
            </span>
          )}
          {person.supervisor === true && (
            <span className="gedaempft mager" data-person-supervisor="">
              {t.personen.supervisorMarke}
            </span>
          )}
        </span>
        <span data-spalte="email" className="leise umbruch">{person.email}</span>
        {/* On the phone the date carries its word ("seit", "zuletzt"); in the
            table the header says it and the cell holds the digits only --
            two spans, the stylesheet shows one of them. */}
        <span data-spalte="beigetreten" className="leise">
          <span className="nur-schmal">{t.personen.beigetretenAm(datumText(person.created_at))}</span>
          <span className="nur-breit">{datumKurz(person.created_at)}</span>
        </span>
        <span data-spalte="zuletzt" className="leise personen__zuletzt">
        <span data-spalte="app">
          <span className="nur-schmal">
            {person.last_seen_at === null
              ? t.personen.nieInApp
              : t.personen.zuletztInAppAm(datumText(person.last_seen_at))}
          </span>
          <span className="nur-breit">
            {person.last_seen_at === null ? t.personen.nieKurz : datumKurz(person.last_seen_at)}
          </span>
        </span>
        <span data-spalte="geschrieben">
          <span className="nur-schmal">
            {person.last_active_at === null
              ? t.personen.nieGeschrieben
              : t.personen.zuletztGeschriebenAm(datumText(person.last_active_at))}
          </span>
          <span className="nur-breit">
            {person.last_active_at === null ? t.personen.nieKurz : datumKurz(person.last_active_at)}
          </span>
        </span>
        </span>
        <span data-spalte="rolle" className="leise">
          {t.personen.rolleKurz[person.role]}
          {person.trips > 0 && ` · ${t.personen.reisen(person.trips)}`}
        </span>
      </button>

      {offen && (
        <div className="personen__bedienung stack">
          {/* Beide Namen stehen da, sobald sie sich unterscheiden -- sonst wüsste
              man nicht mehr, wie dieser Mensch sich selbst nennt, und suchte ihn
              in seiner eigenen Antwort vergeblich. */}
          {person.admin_display_name !== null && (
            <span className="leise">
              {t.personen.nenntSichSelbst(person.display_name)}
            </span>
          )}

          {/* **Nicht abgeblendet, solange irgendwo gesichert wird.** Eine
              Vorsichtsregel, die eine Bedienung überstimmt, ist ein kaputter
              Knopf: das Feld verlöre den Fokus mitten im Tippen, und auf dem
              Telefon ginge die Tastatur zu. Gesichert wird beim Verlassen, und
              zwei Anfragen hintereinander schaden hier nichts. */}
          <label className="field field--dicht">
            <span className="visually-hidden">
              Name für die Verwaltung von {person.display_name}
            </span>
            {/* **Kein Vorschlag aus den Kontakten** (#28). Aus dem Betrieb: über
                der Tastatur stand „Kontakt autom. ausfüllen" -- iOS hält ein Feld
                mit einem Namen darin für ein Kontaktfeld und bietet die eigene
                Adresskartei an. Hier steht aber ein Name, den die *Verwaltung*
                vergibt; ein Vorschlag aus den eigenen Kontakten trifft nie zu.

                Die Leiste selbst bleibt: sie gehört der Tastatur und nicht der
                Seite, und keine Web-Anwendung kann sie ausblenden. Weg ist nur
                der Vorschlag darin.

                `autocorrect`/`autocapitalize` stehen dabei: ein selbst vergebener
                Name ist kein Fließtext, und eine Autokorrektur, die „Binder" zu
                „Binde" macht, ist hier keine Hilfe. */}
            <input
              className="field__input"
              type="text"
              maxLength={80}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              placeholder={t.personen.namensfeld(person.display_name)}
              value={entwurf}
              onChange={(event) => setEntwurf(event.target.value)}
              onBlur={sichere}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  event.currentTarget.blur()
                }
              }}
            />
          </label>

          {self ? (
            // Die eigene Rolle lässt sich nicht ändern -- sonst könnte sich die
            // letzte Verwaltung selbst aussperren.
            <span className="feld__hinweis klein">
              {beschreibung(person.role)} · {t.personen.eigeneRolle}
              {darfSupervisor && ` · ${t.personen.supervisorSelbst}`}
            </span>
          ) : (
            <>
              <label className="field field--dicht">
                <span className="visually-hidden">{t.personen.rolleVon(person.display_name)}</span>
                <select
                  className="field__input"
                  value={person.role}
                  disabled={busy || person.disabled}
                  onChange={(event) => onChange(event.target.value as AccountRole)}
                >
                  <option value="reader">{t.personen.liestMit}</option>
                  <option value="author">{t.personen.darfReisen}</option>
                  <option value="admin">{t.personen.darfVerwalten}</option>
                </select>
              </label>

              {/* Das Merkmal des Supervisors (#62): nur an Admins, nur vergeben
                  von einem Supervisor. Ein Häkchen, kein vierter Eintrag in der
                  Rollenliste -- es liegt quer zur Leiter. */}
              {darfSupervisor && person.role === 'admin' && (
                <label className="row row--wahl">
                  <input
                    type="checkbox"
                    className="wahl__haken"
                    checked={person.supervisor === true}
                    disabled={busy || person.disabled}
                    onChange={(event) => onSupervisor(event.target.checked)}
                    data-person-supervisor-haken=""
                  />
                  <span>
                    <span className="fett">{t.personen.supervisor}</span>
                    <span className="feld__hinweis klein feld__hinweis--block">
                      {t.personen.supervisorHinweis}
                    </span>
                  </span>
                </label>
              )}

              <button
                type="button"
                className="button button--outline button--klein"
                disabled={busy}
                onClick={async () => {
                  // Freigeben braucht keine Rückfrage, Stilllegen schon -- und die
                  // Frage soll sagen, was es nicht tut: Reisen und Nachrichten
                  // bleiben.
                  if (
                    person.disabled ||
                    (await rueckfrage(
                      t.personen.stilllegenFrage(person.display_name),
                      t.personen.stilllegenKurz,
                    ))
                  ) {
                    onDisabled(!person.disabled)
                  }
                }}
              >
                {person.disabled ? t.personen.freigeben : t.personen.stilllegen}
              </button>

              {/*
                  **Der Knopf wird nicht mehr versteckt (#47).**

                  Hier stand „Löschen nur, wo nichts dranhängt" -- und wo etwas
                  dranhing, stand gar nichts, und niemand wusste warum. Seit #47
                  lässt sich auch ein Konto mit Nachrichten und Fotos löschen, mit
                  gezählter Vorschau und abgetippter E-Mail. Die eine Sperre, die
                  bleibt, erklärt sich in der Vorschau selbst: wem eine Reise
                  gehört, dessen Konto nicht.

                  Nur das eigene bleibt ganz außen vor -- dafür gibt es keinen
                  Zustand, in dem es sinnvoll wäre.
              */}
              {!self && <Kontoloeschung person={person} onGeloescht={onGeloescht} />}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function beschreibung(role: AccountRole): string {
  // Ohne Komponente gibt es keinen Haken -- die dokumentierte Ausnahme.
  if (role === 'admin') return WORTLAUT.personen.darfVerwalten
  if (role === 'author') return WORTLAUT.personen.darfReisen
  return WORTLAUT.personen.liestMit
}
